import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { getAllBooking } from "../service/api";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { TablePagination } from "../components/ui/pagination";
import { RefreshCw } from "lucide-react";

const Booking = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [slotDetails, setSlotDetails] = useState<any>([]);
    const [limit] = useState(4);
    const [offset, setOffset] = useState(1);
    const [pagination, setPagination] = useState<any>({});

    useEffect(() => {
        getBooking(offset);
    }, [offset]);

    const getBooking = async (offset: any) => {
        try {
            setLoading(true);
            let res = await getAllBooking(limit, offset);
            setBookings(res.data.data);
            setPagination(res.data.pagination);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }

    const handleView = async (data: any) => {
        setViewModal(true);
        setSlotDetails(data);
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Bookings</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Recent bookings across partners. Open a row to see slot line items and totals.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => getBooking(offset)}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Booking date</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Partner</TableHead>
                                    <TableHead>Turf</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {bookings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center text-muted-foreground py-14">
                                            No bookings to show.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    bookings.map((item: any, index: number) => {
                                        const formattedDate = format(new Date(item.created_at), "dd-MM-yyyy");
                                        return (
                                            <TableRow key={index}>
                                                <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                                <TableCell className="font-mono text-sm tabular-nums">{formattedDate}</TableCell>
                                                <TableCell>{item.user_name}</TableCell>
                                                <TableCell>{item.partner_name}</TableCell>
                                                <TableCell>{item.box_name}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => handleView(item)}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {!loading && pagination?.last_page > 1 && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                        <TablePagination
                            currentPage={offset}
                            totalPages={pagination?.last_page ?? 1}
                            onPageChange={(page) => setOffset(page)}
                        />
                    </div>
                )}
            </Card>

            {/* View booking details dialog */}
            <Dialog open={viewModal} onOpenChange={setViewModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Booking details</DialogTitle>
                        <DialogDescription>Slot line items and total for this booking.</DialogDescription>
                    </DialogHeader>
                    {slotDetails?.slots && (
                        <>
                            <div className="overflow-x-auto rounded-xl border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-10">#</TableHead>
                                            <TableHead>Time</TableHead>
                                            <TableHead className="text-right">Price</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {slotDetails.slots.map((item: any, slotIndex: number) => (
                                            <TableRow key={slotIndex}>
                                                <TableCell>{slotIndex + 1}</TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    {item.from_time} – {item.to_time}
                                                </TableCell>
                                                <TableCell className="text-right font-medium">
                                                    ₹{item.price === "" ? 0 : item.price}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="flex justify-between items-center pt-4 mt-2 border-t">
                                <p className="text-sm font-semibold">Total</p>
                                <p className="text-sm font-semibold tabular-nums">
                                    ₹{slotDetails.slots
                                        .reduce(
                                            (total: number, item: any) =>
                                                total + (isNaN(parseFloat(item.price)) ? 0 : parseFloat(item.price)),
                                            0,
                                        )
                                        .toFixed(2)}
                                </p>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Booking;
